import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Card,
  Box,
  TextField,
  IconButton,
  Button
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ClearIcon from '@material-ui/icons/Clear';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WrapperSeamless from '../../layout-components/WrapperSeamless';
import * as Api from '../../service';
import useStore from '../../context';

const SeoRedirects = () => {
  const { setRemove, setSuccessMsg, setErrorMsg } = useStore();
  const params = new URLSearchParams(window.location.search);
  const prefillFrom = params.get('from') || '';
  const [redirects, setRedirects] = useState([]);
  const [form, setForm] = useState({
    fromUrl: prefillFrom,
    toUrl: '',
    statusCode: 301,
    notes: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    fromUrl: '',
    toUrl: '',
    statusCode: 301,
    notes: ''
  });

  const loadRedirects = useCallback(() => {
    Api.seo.getRedirects()
      .then((res) => setRedirects(Array.isArray(res) ? res : res?.items || []))
      .catch(() => {});
  }, []);

  useEffect(loadRedirects, []);

  const createRedirect = async (e) => {
    e.preventDefault();
    if (!form.fromUrl) return setErrorMsg('fromUrl is required');
    if (!form.toUrl) return setErrorMsg('toUrl is required');
    try {
      await Api.seo.createRedirect(JSON.stringify(form));
      setForm({ fromUrl: '', toUrl: '', statusCode: 301, notes: '' });
      setSuccessMsg('created');
      loadRedirects();
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const exportCsv = async () => {
    try {
      const csv = await Api.seo.exportRedirects();
      const blob = new Blob([typeof csv === 'string' ? csv : JSON.stringify(csv)], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'redirects.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const startEdit = (redirect) => {
    setEditingId(redirect.id || redirect._id);
    setEditForm({
      fromUrl: redirect.fromUrl || '',
      toUrl: redirect.toUrl || '',
      statusCode: redirect.statusCode || 301,
      notes: redirect.notes || ''
    });
  };

  const updateRedirect = async () => {
    try {
      await Api.seo.updateRedirect(editingId, JSON.stringify(editForm));
      setEditingId(null);
      setSuccessMsg('updated');
      loadRedirects();
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  return (
    <Container>
      <WrapperSeamless sectionHeading="Redirect Manager">
        <Card className="shadow-xxl px-4 py-2">
          <Box component="form" onSubmit={createRedirect}>
            <Card className="d-flex p-3 align-items-center">
              <TextField
                variant="outlined"
                className="my-2 mr-2"
                label="From URL"
                fullWidth
                value={form.fromUrl}
                onChange={(e) =>
                  setForm((st) => ({ ...st, fromUrl: e.target.value }))
                }
              />
              <TextField
                variant="outlined"
                className="my-2 mr-2"
                label="To URL"
                fullWidth
                value={form.toUrl}
                onChange={(e) =>
                  setForm((st) => ({ ...st, toUrl: e.target.value }))
                }
              />
              <TextField
                variant="outlined"
                className="my-2 mr-2"
                label="Status Code"
                type="number"
                style={{ maxWidth: 140 }}
                value={form.statusCode}
                onChange={(e) =>
                  setForm((st) => ({ ...st, statusCode: Number(e.target.value) }))
                }
              />
              <TextField
                variant="outlined"
                className="my-2 mr-2"
                label="Notes"
                fullWidth
                value={form.notes}
                onChange={(e) =>
                  setForm((st) => ({ ...st, notes: e.target.value }))
                }
              />
              <IconButton
                className="ml-2"
                style={{ height: 58 }}
                onClick={createRedirect}
              >
                <AddCircleIcon color="primary" fontSize="large" />
              </IconButton>
              <Button className="btn-neutral-first ml-2" onClick={exportCsv}>
                Export CSV
              </Button>
            </Card>
          </Box>

          {redirects?.map((redirect) => (
            <Card
              key={redirect.id || redirect._id}
              className="px-3 my-2 d-flex align-items-center"
            >
              {editingId === (redirect.id || redirect._id) ? (
                <>
                  <TextField
                    variant="outlined"
                    className="my-2 mr-2"
                    label="From URL"
                    fullWidth
                    value={editForm.fromUrl}
                    onChange={(e) =>
                      setEditForm((st) => ({ ...st, fromUrl: e.target.value }))
                    }
                  />
                  <TextField
                    variant="outlined"
                    className="my-2 mr-2"
                    label="To URL"
                    fullWidth
                    value={editForm.toUrl}
                    onChange={(e) =>
                      setEditForm((st) => ({ ...st, toUrl: e.target.value }))
                    }
                  />
                  <TextField
                    variant="outlined"
                    className="my-2 mr-2"
                    label="Status Code"
                    type="number"
                    style={{ maxWidth: 140 }}
                    value={editForm.statusCode}
                    onChange={(e) =>
                      setEditForm((st) => ({
                        ...st,
                        statusCode: Number(e.target.value)
                      }))
                    }
                  />
                  <TextField
                    variant="outlined"
                    className="my-2 mr-2"
                    label="Notes"
                    fullWidth
                    value={editForm.notes}
                    onChange={(e) =>
                      setEditForm((st) => ({ ...st, notes: e.target.value }))
                    }
                  />
                  <Button
                    className="btn-neutral-success mr-2"
                    onClick={updateRedirect}
                  >
                    Save
                  </Button>
                  <Button
                    className="btn-neutral-first"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <span>{redirect.fromUrl}</span>
                  <span className="ml-3 text-black-50">{redirect.toUrl}</span>
                  <span className="ml-3 text-black-50">
                    {redirect.statusCode}
                  </span>
                  {redirect.notes && (
                    <span className="ml-3 text-black-50">{redirect.notes}</span>
                  )}
                  {redirect.hitCount !== undefined && (
                    <span className="ml-3 text-black-50">
                      Hits: {redirect.hitCount}
                    </span>
                  )}
                  {redirect.hits !== undefined && (
                    <span className="ml-3 text-black-50">Hits: {redirect.hits}</span>
                  )}
                  <IconButton
                    className="ml-auto"
                    onClick={() => startEdit(redirect)}
                  >
                    <FontAwesomeIcon icon={['far', 'edit']} />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      setRemove(() => () =>
                        Api.seo
                          .deleteRedirect(redirect.id || redirect._id)
                          .then(loadRedirects)
                      )
                    }
                    className="ml-1"
                  >
                    <ClearIcon color="error" fontSize="medium" />
                  </IconButton>
                </>
              )}
            </Card>
          ))}
        </Card>
      </WrapperSeamless>
    </Container>
  );
};

export default SeoRedirects;
